import { SEASONS, TURNS_PER_SEASON } from "../../models/config/balance.ts";
import { DAY_NIGHT_PHASES, type DayNightPhase } from "../../models/dayNightPhase.ts";
import type { Season } from "../../models/season.ts";

export const WEATHER_TURNS_PER_SEASON = TURNS_PER_SEASON;

const SEASON_PHASE_WEIGHTS: Record<
  Season,
  readonly [number, number, number, number]
> = {
  spring: [2, 1, 2, 1],
  summer: [2, 1, 1, 2],
  autumn: [1, 2, 1, 2],
  winter: [1, 2, 2, 1],
};

function normalizeWeatherTurn(turn: number): number {
  if (turn < 1) {
    return 1;
  }
  return turn;
}

export function getSeasonForWeatherTurn(turn: number): Season {
  const normalized = normalizeWeatherTurn(turn);
  const index =
    Math.floor((normalized - 1) / WEATHER_TURNS_PER_SEASON) % SEASONS.length;
  return SEASONS[index]!;
}

export function getTurnIndexWithinWeatherSeason(turn: number): number {
  const normalized = normalizeWeatherTurn(turn);
  return (normalized - 1) % WEATHER_TURNS_PER_SEASON;
}

function getDayCycleLength(season: Season): number {
  const weights = SEASON_PHASE_WEIGHTS[season];
  return weights.reduce((sum, weight) => sum + weight, 0);
}

function offsetInDayCycle(season: Season, turnIndexInSeason: number): number {
  const cycleLength = getDayCycleLength(season);
  return ((turnIndexInSeason % cycleLength) + cycleLength) % cycleLength;
}

export function getDayNightPhaseForWeatherTurn(turn: number): DayNightPhase {
  const season = getSeasonForWeatherTurn(turn);
  const turnIndexInSeason = getTurnIndexWithinWeatherSeason(turn);
  const offset = offsetInDayCycle(season, turnIndexInSeason);
  const weights = SEASON_PHASE_WEIGHTS[season];
  let cumulative = 0;
  for (let index = 0; index < DAY_NIGHT_PHASES.length; index++) {
    cumulative += weights[index]!;
    if (offset < cumulative) {
      return DAY_NIGHT_PHASES[index]!;
    }
  }
  return DAY_NIGHT_PHASES[0]!;
}

export function getCycleProgressForWeatherTurn(turn: number): number {
  const season = getSeasonForWeatherTurn(turn);
  const turnIndexInSeason = getTurnIndexWithinWeatherSeason(turn);
  const cycleLength = getDayCycleLength(season);
  const offset = offsetInDayCycle(season, turnIndexInSeason);
  return offset / cycleLength;
}

function assertWeatherCalendarInvariants(): void {
  for (const season of SEASONS) {
    const cycleLength = getDayCycleLength(season);
    if (WEATHER_TURNS_PER_SEASON % cycleLength !== 0) {
      throw new Error(
        `Season ${season} day cycle length ${cycleLength} must divide WEATHER_TURNS_PER_SEASON ${WEATHER_TURNS_PER_SEASON}`,
      );
    }
  }
}

assertWeatherCalendarInvariants();
